export const GOVERNANCE_QUEUE_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'issues', label: 'Qualidade' },
  { value: 'duplicates', label: 'Duplicidades' },
  { value: 'fraud', label: 'Fraude' },
  { value: 'scores', label: 'Scores' },
];

export const FRAUD_OUTCOME_OPTIONS = [
  { value: 'confirmed', label: 'Confirmar fraude' },
  { value: 'cleared', label: 'Limpar falso positivo' },
];

export const QUARANTINE_SCOPE_OPTIONS = [
  { value: 'feed', label: 'Feed' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'listing', label: 'Anuncio' },
  { value: 'orphan_lead', label: 'Lead orfao' },
];

const field = (object, snakeKey, camelKey = snakeKey) =>
  object?.[snakeKey] ?? object?.[camelKey];

export const normalizeObject = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const normalizeArray = value => (Array.isArray(value) ? value : []);

export const percentageLabel = value => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '-';
  return `${Math.round(number * 100)}%`;
};

export const formatBackendError = error => {
  const status = error?.response?.status;
  const data = normalizeObject(error?.response?.data);
  const errors = Array.isArray(data.errors)
    ? data.errors
    : [data.error, data.message].filter(Boolean);

  return {
    status,
    code: data.code || '',
    conflict: status === 409 || data.code === 'merge_conflict',
    message: errors.length
      ? errors.join(' ')
      : 'Nao foi possivel concluir a operacao.',
  };
};

const normalizeSummaryGroup = value =>
  Object.entries(normalizeObject(value)).reduce((memo, [key, item]) => {
    memo[key] = Number(item || 0);
    return memo;
  }, {});

export const normalizeEntity = entity => {
  const payload = normalizeObject(entity);
  return {
    type: field(payload, 'type') || field(payload, 'entity_type') || '',
    id: field(payload, 'id'),
    label: field(payload, 'label') || `${field(payload, 'type') || 'Registro'}`,
    subtitle: field(payload, 'subtitle') || '',
    routeName: field(payload, 'route_name', 'routeName') || '',
    missing: Boolean(field(payload, 'missing')),
  };
};

const normalizeAlert = alert => {
  const payload = normalizeObject(alert);
  return {
    id: payload.id,
    status: payload.status || '',
    title: payload.title || `Alerta #${payload.id}`,
    contactId: field(payload, 'contact_id', 'contactId'),
    contactName: field(payload, 'contact_name', 'contactName') || '',
    filters: normalizeObject(payload.filters),
    updatedAt: field(payload, 'updated_at', 'updatedAt') || '',
  };
};

const normalizeOperation = operation => {
  const payload = normalizeObject(operation);
  if (!payload.id) return null;

  return {
    id: payload.id,
    status: payload.status || '',
    entityType: field(payload, 'entity_type', 'entityType') || '',
    winnerEntityId: field(payload, 'winner_entity_id', 'winnerEntityId'),
    loserEntityId: field(payload, 'loser_entity_id', 'loserEntityId'),
    duplicateCandidateId: field(
      payload,
      'duplicate_candidate_id',
      'duplicateCandidateId'
    ),
    matchType: field(payload, 'match_type', 'matchType') || '',
    reversible: Boolean(payload.reversible),
    appliedAt: field(payload, 'applied_at', 'appliedAt') || '',
    revertedAt: field(payload, 'reverted_at', 'revertedAt') || '',
    revertReason: field(payload, 'revert_reason', 'revertReason') || '',
    reviewNote: field(payload, 'review_note', 'reviewNote') || '',
    preview: normalizeObject(payload.preview),
    affectedReferences: normalizeObject(
      field(payload, 'affected_references', 'affectedReferences')
    ),
    revertPlan: normalizeArray(field(payload, 'revert_plan', 'revertPlan')),
  };
};

const normalizeHumanApproval = value => {
  const payload = normalizeObject(value);
  return {
    required: Boolean(payload.required),
    status: payload.status || '',
    reviewerUserId: field(payload, 'reviewer_user_id', 'reviewerUserId'),
    reasonRequired: Boolean(
      field(payload, 'reason_required', 'reasonRequired')
    ),
  };
};

export function normalizeDuplicateCandidate(candidate) {
  const payload = normalizeObject(candidate);
  const matchScore = Number(field(payload, 'match_score', 'matchScore') || 0);
  return {
    kind: 'duplicate',
    key: `duplicate:${payload.id}`,
    id: payload.id,
    status: payload.status || '',
    resolution: payload.resolution || '',
    entityType: field(payload, 'entity_type', 'entityType') || '',
    primaryEntityId: field(payload, 'primary_entity_id', 'primaryEntityId'),
    duplicateEntityId: field(
      payload,
      'duplicate_entity_id',
      'duplicateEntityId'
    ),
    primaryEntity: normalizeEntity(
      field(payload, 'primary_entity', 'primaryEntity')
    ),
    duplicateEntity: normalizeEntity(
      field(payload, 'duplicate_entity', 'duplicateEntity')
    ),
    matchType: field(payload, 'match_type', 'matchType') || '',
    matchScore,
    matchScoreLabel: percentageLabel(matchScore),
    detectorKey: field(payload, 'detector_key', 'detectorKey') || '',
    detectorVersion:
      field(payload, 'detector_version', 'detectorVersion') || '',
    autoMergeable: Boolean(field(payload, 'auto_mergeable', 'autoMergeable')),
    requiresHumanReview: Boolean(
      field(payload, 'requires_human_review', 'requiresHumanReview')
    ),
    signals: normalizeObject(payload.signals),
    discriminators: normalizeObject(payload.discriminators),
    metadata: normalizeObject(payload.metadata),
    reviewNote: field(payload, 'review_note', 'reviewNote') || '',
    partial: Boolean(payload.partial),
    linkedAlerts: normalizeArray(
      field(payload, 'linked_alerts', 'linkedAlerts')
    ).map(normalizeAlert),
    latestMergeOperation: normalizeOperation(
      field(payload, 'latest_merge_operation', 'latestMergeOperation')
    ),
    recommendation: payload.recommendation || '',
    humanApproval: normalizeHumanApproval(
      field(payload, 'human_approval', 'humanApproval')
    ),
    availableActions: normalizeArray(
      field(payload, 'available_actions', 'availableActions')
    ),
    updatedAt: field(payload, 'updated_at', 'updatedAt') || '',
  };
}

export function normalizeFraudSignal(signal) {
  const payload = normalizeObject(signal);
  return {
    kind: 'fraud',
    key: `fraud:${payload.id}`,
    id: payload.id,
    entityType: field(payload, 'entity_type', 'entityType') || '',
    entityId: field(payload, 'entity_id', 'entityId'),
    entity: normalizeEntity(payload.entity),
    signalType: field(payload, 'signal_type', 'signalType') || '',
    severity: payload.severity || '',
    disposition: payload.disposition || '',
    requiresReview: Boolean(
      field(payload, 'requires_review', 'requiresReview')
    ),
    quarantineScope:
      field(payload, 'quarantine_scope', 'quarantineScope') || '',
    scores: normalizeObject(payload.scores),
    scoreComponents: normalizeObject(
      field(payload, 'score_components', 'scoreComponents')
    ),
    dataUsed: normalizeObject(field(payload, 'data_used', 'dataUsed')),
    evidence: normalizeObject(payload.evidence),
    recommendation: payload.recommendation || '',
    availableActions: normalizeArray(
      field(payload, 'available_actions', 'availableActions')
    ),
    detectedAt: field(payload, 'detected_at', 'detectedAt') || '',
    reviewedAt: field(payload, 'reviewed_at', 'reviewedAt') || '',
    reviewOutcome: field(payload, 'review_outcome', 'reviewOutcome') || '',
  };
}

export function normalizeQualityScore(score) {
  const payload = normalizeObject(score);
  return {
    kind: 'score',
    key: `score:${payload.id}`,
    id: payload.id,
    entityType: field(payload, 'entity_type', 'entityType') || '',
    entityId: field(payload, 'entity_id', 'entityId'),
    entity: normalizeEntity(payload.entity),
    scoreKind: field(payload, 'score_kind', 'scoreKind') || '',
    dimensions: normalizeObject(payload.dimensions),
    compositeScore: field(payload, 'composite_score', 'compositeScore'),
    compositeScoreLabel: percentageLabel(
      field(payload, 'composite_score', 'compositeScore')
    ),
    measurement: normalizeObject(payload.measurement),
    version: normalizeObject(payload.version),
    source: payload.source || '',
    dataUsed: normalizeObject(field(payload, 'data_used', 'dataUsed')),
    metadata: normalizeObject(payload.metadata),
    recommendation: payload.recommendation || '',
    computedAt: field(payload, 'computed_at', 'computedAt') || '',
  };
}

export function normalizeIssue(issue) {
  const payload = normalizeObject(issue);
  return {
    kind: 'issue',
    key: `issue:${payload.id}`,
    id: payload.id,
    entityType: field(payload, 'entity_type', 'entityType') || '',
    entityId: field(payload, 'entity_id', 'entityId'),
    entity: normalizeEntity(payload.entity),
    field: payload.field || '',
    issueType: field(payload, 'issue_type', 'issueType') || payload.rule_key,
    ruleKey: field(payload, 'rule_key', 'ruleKey') || '',
    severity: payload.severity || '',
    status: payload.status || '',
    detectionSource:
      field(payload, 'detection_source', 'detectionSource') || '',
    ownerUserId: field(payload, 'owner_user_id', 'ownerUserId'),
    resolution: payload.resolution || '',
    resolutionNote: field(payload, 'resolution_note', 'resolutionNote') || '',
    recommendation: payload.recommendation || '',
    evidence: normalizeObject(
      field(payload, 'detection_evidence', 'detectionEvidence')
    ),
    metadata: normalizeObject(payload.metadata),
    linkedAlerts: normalizeArray(
      field(payload, 'linked_alerts', 'linkedAlerts')
    ).map(normalizeAlert),
    relatedFraudSignals: normalizeArray(
      field(payload, 'related_fraud_signals', 'relatedFraudSignals')
    ).map(normalizeFraudSignal),
    relatedQualityScores: normalizeArray(
      field(payload, 'related_quality_scores', 'relatedQualityScores')
    ).map(normalizeQualityScore),
    relatedDuplicateCandidates: normalizeArray(
      field(
        payload,
        'related_duplicate_candidates',
        'relatedDuplicateCandidates'
      )
    ).map(normalizeDuplicateCandidate),
    humanApproval: normalizeHumanApproval(
      field(payload, 'human_approval', 'humanApproval')
    ),
    updatedAt: field(payload, 'updated_at', 'updatedAt') || '',
  };
}

export const normalizeGovernancePayload = payload => {
  const data = normalizeObject(payload?.data || payload);
  const summary = normalizeObject(data.summary);
  const issues = normalizeArray(data.issues).map(normalizeIssue);
  const duplicateCandidates = normalizeArray(
    field(data, 'duplicate_candidates', 'duplicateCandidates')
  ).map(normalizeDuplicateCandidate);
  const fraudSignals = normalizeArray(
    field(data, 'fraud_signals', 'fraudSignals')
  ).map(normalizeFraudSignal);
  const qualityScores = normalizeArray(
    field(data, 'quality_scores', 'qualityScores')
  ).map(normalizeQualityScore);

  return {
    summary: {
      issues: {
        byStatus: normalizeSummaryGroup(summary.issues?.by_status),
        bySeverity: normalizeSummaryGroup(summary.issues?.by_severity),
        openCount: Number(summary.issues?.open_count || 0),
        unassignedCount: Number(summary.issues?.unassigned_count || 0),
      },
      duplicateCandidates: {
        byStatus: normalizeSummaryGroup(
          summary.duplicate_candidates?.by_status
        ),
        byEntityType: normalizeSummaryGroup(
          summary.duplicate_candidates?.by_entity_type
        ),
        reviewRequiredCount: Number(
          summary.duplicate_candidates?.review_required_count || 0
        ),
        reversibleMergeCount: Number(
          summary.duplicate_candidates?.reversible_merge_count || 0
        ),
      },
      fraudSignals: {
        byDisposition: normalizeSummaryGroup(
          summary.fraud_signals?.by_disposition
        ),
        bySeverity: normalizeSummaryGroup(summary.fraud_signals?.by_severity),
        reviewRequiredCount: Number(
          summary.fraud_signals?.review_required_count || 0
        ),
        quarantinedCount: Number(summary.fraud_signals?.quarantined_count || 0),
      },
      qualityScores: {
        byKind: normalizeSummaryGroup(summary.quality_scores?.by_kind),
        latestComputedAt: summary.quality_scores?.latest_computed_at || '',
      },
      linkedAlerts: {
        activeCount: Number(summary.linked_alerts?.active_count || 0),
        linkedToConsoleCount: Number(
          summary.linked_alerts?.linked_to_console_count || 0
        ),
      },
    },
    issues,
    duplicateCandidates,
    fraudSignals,
    qualityScores,
    queueItems: [
      ...issues,
      ...duplicateCandidates,
      ...fraudSignals,
      ...qualityScores,
    ],
  };
};

export const entriesForDisplay = value =>
  Object.entries(normalizeObject(value)).map(([key, item]) => ({
    key,
    value: Array.isArray(item) ? item.join(' / ') : String(item),
  }));
